import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete, Box, Button, CircularProgress, Divider,
  IconButton, Paper, TextField, ToggleButton, ToggleButtonGroup,
  Tooltip, Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import Layout from '../components/Layout';
import ProjectService from '../services/ProjectService';
import UserService from '../services/UserService';
import { AuthContext } from '../contexts/AuthContext';
import Roles from '../config/roles';
import ConfirmModal from './modal/ConfirmModal';
import TTWeekView from './sub-components/tt/TTWeekView';
import TTDayView from './sub-components/tt/TTDayView';
import TTMonthView from './sub-components/tt/TTMonthView';
import TTAgendaView from './sub-components/tt/TTAgendaView';
import TTEntryDrawer from './sub-components/tt/TTEntryDrawer';
import AbsenceService from '../services/AbsenceService';

// ── Project color palette ────────────────────────────────────────
const PALETTE = ['#427898','#8B5CF6','#10B981','#F59E0B','#EF4444','#0EA5E9','#EC4899','#84CC16'];
const projectColor = (id) => PALETTE[(id || 0) % PALETTE.length];

// ── Date helpers ─────────────────────────────────────────────────
const pad = (n) => String(n).padStart(2, '0');
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function getRangeForView(view, anchor) {
  const d = new Date(anchor);
  if (view === 'day') {
    return { start: toISO(d), end: toISO(d) };
  }
  if (view === 'week') {
    const day = d.getDay();
    const diffToMon = day === 0 ? -6 : 1 - day;
    const mon = new Date(d); mon.setDate(d.getDate() + diffToMon);
    const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
    return { start: toISO(mon), end: toISO(sun) };
  }
  if (view === 'month') {
    const start = new Date(d.getFullYear(), d.getMonth(), 1);
    const end   = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return { start: toISO(start), end: toISO(end) };
  }
  // agenda: ±30 days
  const start = new Date(d); start.setDate(d.getDate() - 7);
  const end   = new Date(d); end.setDate(d.getDate() + 30);
  return { start: toISO(start), end: toISO(end) };
}

function formatPeriodLabel(view, anchor) {
  if (view === 'day') {
    return anchor.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  }
  if (view === 'week') {
    const { start, end } = getRangeForView('week', anchor);
    const s = new Date(start + 'T00:00:00');
    const e = new Date(end   + 'T00:00:00');
    const sLabel = s.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    const eLabel = e.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    return `${sLabel} – ${eLabel}`;
  }
  if (view === 'month') {
    return anchor.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  }
  return 'Upcoming';
}

function stepDate(view, anchor, dir) {
  const d = new Date(anchor);
  if (view === 'day')   d.setDate(d.getDate() + dir);
  if (view === 'week')  d.setDate(d.getDate() + dir * 7);
  if (view === 'month') d.setMonth(d.getMonth() + dir);
  if (view === 'agenda') d.setDate(d.getDate() + dir * 14);
  return d;
}

// ── Main component ───────────────────────────────────────────────
export default function TimeTracking() {
  const { role } = useContext(AuthContext);
  const isAdmin = role === Roles.ADMIN;

  const [view, setView]               = useState('week');
  const [anchor, setAnchor]           = useState(new Date());
  const [entries, setEntries]         = useState([]);
  const [projects, setProjects]       = useState([]);
  const [users, setUsers]             = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading]         = useState(false);

  // Drawer state
  const [drawerOpen, setDrawerOpen]   = useState(false);
  const [editEntry, setEditEntry]     = useState(null);
  const [drawerDate, setDrawerDate]   = useState(null);
  const [drawerTime, setDrawerTime]   = useState(null);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting]       = useState(false);
  const [absences, setAbsences]       = useState([]);

  // ── Load reference data once ─────────────────────────────────
  useEffect(() => {
    ProjectService.getAllProjectsFlat().then(setProjects).catch(console.error);
    if (isAdmin) {
      UserService.getAllUsers().then(setUsers).catch(console.error);
    }
  }, [isAdmin]);

  // ── Fetch entries + absences when view/anchor/user changes ────
  useEffect(() => {
    const { start, end } = getRangeForView(view, anchor);
    setLoading(true);
    ProjectService.getTimeTrackingEntries(start, end, selectedUser?.id || null)
      .then(setEntries)
      .catch(console.error)
      .finally(() => setLoading(false));
    AbsenceService.getInRange(start, end)
      .then(data => {
        // If a user is selected, filter to that user's absences only
        if (selectedUser) {
          setAbsences((data || []).filter(a => a.employeeId === selectedUser.id));
        } else {
          setAbsences(data || []);
        }
      })
      .catch(() => setAbsences([]));
  }, [view, anchor, selectedUser]);

  // ── Summary stats ────────────────────────────────────────────
  const summary = useMemo(() => {
    const total    = entries.reduce((s, e) => s + (e.workingHours || 0), 0);
    const billable = entries.filter(e => e.billable !== false).reduce((s, e) => s + (e.workingHours || 0), 0);
    return { total, billable, internal: total - billable };
  }, [entries]);

  const fmtH = (h) => h % 1 === 0 ? `${h}h` : `${h.toFixed(1)}h`;

  // ── Handlers ────────────────────────────────────────────────
  const openAdd = (date = null, startTime = null) => {
    setEditEntry(null);
    setDrawerDate(date);
    setDrawerTime(startTime);
    setDrawerOpen(true);
  };

  const openEdit = (entry) => {
    setEditEntry(entry);
    setDrawerDate(null);
    setDrawerOpen(true);
  };

  const handleSave = async (data) => {
    try {
      if (data.id) {
        const updated = await ProjectService.updateProjectEntry(data.id, data);
        setEntries(prev => prev.map(e => e.id === data.id ? { ...e, ...updated, projectName: e.projectName, projectId: e.projectId } : e));
      } else {
        const created = await ProjectService.createProjectEntry(data);
        const proj = projects.find(p => p.id === data.project);
        setEntries(prev => [...prev, { ...created, projectId: data.project, projectName: proj?.name || '' }]);
      }
      setDrawerOpen(false);
    } catch (err) {
      console.error('Failed to save entry', err);
    }
  };

  // ── Drag/resize inline update (no drawer) ────────────────
  const handleUpdate = async (entry) => {
    try {
      const data = {
        project:      entry.projectId,
        description:  entry.description,
        workingHours: entry.workingHours,
        entryDate:    entry.entryDate,
        billable:     entry.billable !== false,
        startTime:    entry.startTime || null,
        endTime:      entry.endTime   || null,
        userId:       entry.userId,
      };
      const updated = await ProjectService.updateProjectEntry(entry.id, data);
      setEntries(prev => prev.map(e =>
        e.id === entry.id ? { ...e, ...updated, projectName: e.projectName, projectId: e.projectId } : e
      ));
    } catch (err) {
      console.error('Failed to update entry', err);
    }
  };

  const confirmDelete = (entry) => setDeleteTarget(entry);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await ProjectService.deleteProjectEntry(deleteTarget.id);
      setEntries(prev => prev.filter(e => e.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      console.error('Failed to delete entry', err);
    } finally {
      setDeleting(false);
    }
  };

  const handleMonthDayClick = (day) => {
    setAnchor(day);
    setView('day');
  };

  // ── Render ───────────────────────────────────────────────────
  return (
    <Layout>
      <Box sx={{ bgcolor: '#F8FAFC', minHeight: '100vh' }}>

        {/* ── Sticky header ── */}
        <Box sx={{
          position: 'sticky', top: 0, zIndex: 10,
          bgcolor: '#fff', borderBottom: '1px solid #EEF2F6',
        }}>
          {/* Main header row */}
          <Box sx={{ px: 3, py: 1.5, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>

            {/* Nav arrows + period label */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton size="small" onClick={() => setAnchor(d => stepDate(view, d, -1))}
                sx={{ color: '#64748B', '&:hover': { bgcolor: '#F1F5F9' } }}>
                <ChevronLeftIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <IconButton size="small" onClick={() => setAnchor(d => stepDate(view, d, 1))}
                sx={{ color: '#64748B', '&:hover': { bgcolor: '#F1F5F9' } }}>
                <ChevronRightIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#0F172A', minWidth: 180 }}>
                {formatPeriodLabel(view, anchor)}
              </Typography>
              <Button size="small" onClick={() => setAnchor(new Date())}
                sx={{ textTransform: 'none', fontWeight: 500, fontSize: 12, color: '#64748B', px: 1, ml: 0.5, '&:hover': { bgcolor: '#F1F5F9' } }}>
                Today
              </Button>
            </Box>

            <Box sx={{ flex: 1 }} />

            {/* View switcher */}
            <ToggleButtonGroup value={view} exclusive onChange={(_, v) => v && setView(v)} size="small"
              sx={{ '& .MuiToggleButton-root': { px: 1.5, py: 0.5, textTransform: 'none', fontSize: 13, fontWeight: 500, border: '1px solid #E2E8F0', color: '#64748B', '&.Mui-selected': { bgcolor: '#EFF6FF', color: '#427898', borderColor: '#BFDBFE', fontWeight: 600 } } }}>
              <ToggleButton value="day">Day</ToggleButton>
              <ToggleButton value="week">Week</ToggleButton>
              <ToggleButton value="month">Month</ToggleButton>
              <ToggleButton value="agenda">Agenda</ToggleButton>
            </ToggleButtonGroup>

            <Divider orientation="vertical" flexItem sx={{ height: 28, alignSelf: 'center' }} />

            {/* Admin user picker */}
            {isAdmin && users.length > 0 && (
              <Autocomplete
                options={users}
                getOptionLabel={u => `${u.firstName} ${u.lastName}`}
                value={selectedUser}
                onChange={(_, v) => setSelectedUser(v)}
                renderInput={params => <TextField {...params} size="small" placeholder="All employees" sx={{ width: 200, '& .MuiInputBase-root': { fontSize: 13 } }} />}
                clearOnEscape
              />
            )}

            {/* Log Time button */}
            <Button
              variant="contained"
              startIcon={<AddIcon sx={{ fontSize: '16px !important' }} />}
              onClick={() => openAdd(null)}
              sx={{ bgcolor: '#427898', textTransform: 'none', fontWeight: 600, fontSize: 13, borderRadius: 1.5, boxShadow: 'none', px: 2, '&:hover': { boxShadow: 'none', bgcolor: '#355f77' } }}
            >
              Log Time
            </Button>
          </Box>

          {/* Summary bar */}
          {summary.total > 0 && (
            <Box sx={{ px: 3, py: 0.75, bgcolor: '#F8FAFC', borderTop: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', gap: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#427898' }} />
                <Typography sx={{ fontSize: 12, color: '#475569', fontWeight: 500 }}>
                  <b>{fmtH(summary.total)}</b> logged
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#16A34A' }} />
                <Typography sx={{ fontSize: 12, color: '#475569' }}>
                  <b>{fmtH(summary.billable)}</b> billable
                </Typography>
              </Box>
              {summary.internal > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#CBD5E1' }} />
                  <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>
                    <b>{fmtH(summary.internal)}</b> internal
                  </Typography>
                </Box>
              )}
              {loading && <CircularProgress size={12} sx={{ color: '#CBD5E1', ml: 1 }} />}
            </Box>
          )}
        </Box>

        {/* ── View content ── */}
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: view === 'month' ? 1100 : 1280, mx: 'auto' }}>
          {loading && entries.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
              <CircularProgress sx={{ color: '#427898' }} />
            </Box>
          ) : (
            <>
              {view === 'week' && (
                <TTWeekView
                  currentDate={anchor}
                  entries={entries}
                  absences={absences}
                  projectColor={projectColor}
                  onAddDay={openAdd}
                  onEdit={openEdit}
                  onDelete={confirmDelete}
                  onUpdate={handleUpdate}
                />
              )}
              {view === 'day' && (
                <TTDayView
                  currentDate={anchor}
                  entries={entries}
                  absences={absences}
                  projectColor={projectColor}
                  onAddDay={openAdd}
                  onEdit={openEdit}
                  onDelete={confirmDelete}
                  onUpdate={handleUpdate}
                />
              )}
              {view === 'month' && (
                <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff', p: 2.5 }}>
                  <TTMonthView
                    currentDate={anchor}
                    entries={entries}
                    projectColor={projectColor}
                    onDayClick={handleMonthDayClick}
                  />
                </Paper>
              )}
              {view === 'agenda' && (
                <TTAgendaView
                  entries={entries}
                  projectColor={projectColor}
                  onEdit={openEdit}
                  onDelete={confirmDelete}
                  onAddDay={openAdd}
                />
              )}
            </>
          )}
        </Box>

        {/* ── Entry Drawer ── */}
        <TTEntryDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onSave={handleSave}
          initialEntry={editEntry}
          initialDate={drawerDate}
          initialTime={drawerTime}
          projects={projects}
          users={users}
          isAdmin={isAdmin}
        />

        {/* ── Delete confirm ── */}
        <ConfirmModal
          open={Boolean(deleteTarget)}
          onClose={() => !deleting && setDeleteTarget(null)}
          onConfirm={handleDelete}
          question={`Delete this entry (${deleteTarget?.workingHours}h on ${deleteTarget?.projectName})?`}
          isLoading={deleting}
        />
      </Box>
    </Layout>
  );
}
